// The two registries of the Variables window (spec 6.10.4): where a value lives (location types) and how the
// app gets it (delivery platforms). Every text, icon and tone that depends on a store or a platform comes from
// here; nothing else branches on a provider, a location type or a platform name. Unknown types and platforms
// get a generic entry built from the server's English fallback texts.
import {
  Archive,
  Braces,
  Cable,
  KeyRound,
  Link2,
  Lock,
  Network,
  Settings2,
  Shield,
  SlidersHorizontal,
  SquareAsterisk,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type {
  DeliveryMode,
  RotationInfo,
  SecretDriftKind,
  SettingDelivery,
  SettingLocation,
} from '../api/types';
import type { Tone } from '../components/presentation';
import { formatDay } from '../lib/format';
import { strings } from '../strings';

const s = strings.variables;
const v = strings.vaults;

/** Where a value lives. Key: `location.type`. */
export interface LocationTypeDefinition {
  type: string;
  label(location: Pick<SettingLocation, 'label' | 'container'>): string;
  icon: LucideIcon;
  tone: Tone | 'violet';
  /** The details pane's label for the name in source; null when the location has no name. */
  nameLabel: string | null;
  secret: boolean;
  /** Short form of a version for cells. */
  formatVersion(version: string): string;
  /** The size limit's name in the Set value popover. */
  limitLabel: string;
  /** Explanations of drift that differ per location. */
  driftText?: Partial<Record<SecretDriftKind, string>>;
  /**
   * The reveal confirmation's sentence of what is recorded and read (spec 6.10.18): the vault is the
   * location's container, `account` the deployment's read identity (null: "the deployment's cloud account").
   */
  revealText(
    location: Pick<SettingLocation, 'label' | 'container'>,
    account: string | null,
  ): RevealTextPart[];
  /** Why a disabled secret can't be revealed. */
  disabledRevealText: string;
  /** True for the entries the frontend ships; false for the generic fallback. */
  known: boolean;
  /** Managed vaults: how long a deleted vault and its secrets stay recoverable (the destroy dialog, spec 6.4). */
  softDeleteDays?: number;
  /** Managed vaults: what a destroy does to the vault (spec 6.4 Destroy). */
  vaultDeletion?(vault: { name: string; region: string }, deployment: string, until: string): string;
  // ---- Feature spec 15 (spec 6.10.19.1): members the namespace vaults fill; absent on the others. ----
  /** The short name for sentences ("Secrets Manager"). */
  shortLabel?: string;
  /** What the provider calls its metadata ("labels" on GCP); default tags. */
  metadataNoun?: 'tags' | 'labels';
  /** Namespace vaults: the destroy template's mechanism and caveat (6.4); its secrets outlive the vault. */
  namespaceDeletion?: { mechanism: string; caveat: string };
  /** The Rotation column and the details' rotation line. */
  rotationText?(rotation: RotationInfo): { text: string; help: string | null };
  /** The details' note when the declared spelling isn't the native name. */
  caseNote?: string;
  /** The vault mode field's placement sentence. */
  placementText?(region: string): string;
  /** The vault `not-created` banner. */
  prepareText?(deployment: string, prefix: string, region: string, project: string): string;
}

/** A part of a registry sentence: words, or a name shown in bold. */
export type RevealTextPart = string | { strong: string };

const r = strings.reveal;

/** "as the deployment's cloud account <account>." */
function readsAs(account: string | null): RevealTextPart[] {
  return account ? [` ${r.readsAs} `, { strong: account }, '.'] : [` ${r.readsAs}.`];
}

function genericRevealText(
  location: Pick<SettingLocation, 'label' | 'container'>,
  account: string | null,
): RevealTextPart[] {
  return [
    r.generic.before(location.label),
    ...(location.container ? [' ', { strong: location.container }] : []),
    ...readsAs(account),
  ];
}

/** How the app gets the value. Key: `delivery.platform`. */
export interface DeliveryTypeDefinition {
  platform: string | null;
  label(mode: DeliveryMode, delivery: SettingDelivery): string;
  /** When a new value reaches the apps. */
  pickupText(apps: readonly string[], delivery: SettingDelivery): string;
  /** The Set value popover's "now" checkbox; null when the delivery has none. */
  nowAction(
    apps: readonly string[],
    delivery: SettingDelivery,
  ): { label: string; disruption: string; default: 'from-on-change' | 'on' } | null;
  /** Who else can read a value copied into the app (store copies). */
  readersText(app: string, delivery: SettingDelivery): string | null;
  /** What Refresh references does on this platform (the button's explanation). */
  refreshText(app: string): string;
  known: boolean;
  // ---- Feature spec 15 (spec 6.10.19.2) ----
  /** The rollout action's short label for buttons and menus ("Roll out new tasks", "Restart web"). */
  nowActionShortLabel?(app: string): string;
  /** Whether the rollout is confirmed before it runs (App Service: a restart is disruptive). */
  nowActionConfirm?: boolean;
  /** Under the checked rollout box when the secret's On change is Wait. */
  nowActionDefaultReason?(secret: string): string;
  nowActionDoneText?(apps: readonly string[]): string;
  nowActionFailedText?(app: string, error: string): string;
  /** Per-app statuses: their words. */
  statusText?: {
    pending: string;
    resolved: string;
    resolvedHelp(app: string, checked: string, read: string): string;
  };
  rawStatusText?: Record<string, string>;
  referenceFailureText?(app: string): string;
  grantDelayText?(app: string): string;
  olderVersion?: { label: string; help(app: string, read: string): string };
  rollingOutText?(app: string, ago: string, by: string): string;
  settingsChangeText?(app: string): string;
  refreshFallbackText?: string;
  /** The `app-restarted` activity entry's verb phrase. */
  activityAction?(app: string): string;
}

const asIs = (version: string) => version;

type Defaulted = 'formatVersion' | 'limitLabel' | 'revealText' | 'disabledRevealText';

function entry(
  definition: Omit<LocationTypeDefinition, Defaulted | 'known'> &
    Partial<Pick<LocationTypeDefinition, Defaulted>>,
): LocationTypeDefinition {
  return {
    formatVersion: asIs,
    limitLabel: s.limits.generic,
    revealText: genericRevealText,
    disabledRevealText: r.generic.disabled,
    known: true,
    ...definition,
  };
}

const STORE_KINDS = ['environment', 'azure-key-vault', 'aws-secrets-manager', 'gcp-secret-manager'];

const storeEntry = (kind: string) =>
  entry({
    type: `store:${kind}`,
    label: (l) => s.locationLabels.store(l.container ?? ''),
    icon: Archive,
    tone: 'warning',
    nameLabel: s.nameLabels.storeName,
    secret: true,
  });

export const LOCATION_TYPES: readonly LocationTypeDefinition[] = [
  entry({
    type: 'plain',
    label: () => s.locationLabels.plain,
    icon: Braces,
    tone: 'neutral',
    nameLabel: null,
    secret: false,
  }),
  entry({
    type: 'expression',
    label: () => s.locationLabels.expression,
    icon: Braces,
    tone: 'neutral',
    nameLabel: s.nameLabels.template,
    secret: false,
  }),
  entry({
    type: 'parameter',
    label: () => s.locationLabels.parameter,
    icon: SlidersHorizontal,
    tone: 'info',
    nameLabel: s.nameLabels.parameter,
    secret: false,
  }),
  entry({
    type: 'output',
    label: (l) => s.locationLabels.output(l.container ?? ''),
    icon: Link2,
    tone: 'neutral',
    nameLabel: s.nameLabels.output,
    secret: false,
  }),
  entry({
    type: 'connection',
    label: (l) => s.locationLabels.connection(l.container ?? ''),
    icon: Cable,
    tone: 'success',
    nameLabel: s.nameLabels.output,
    secret: false,
  }),
  entry({
    type: 'trigger',
    label: (l) => s.locationLabels.trigger(l.container ?? ''),
    icon: Zap,
    tone: 'violet',
    nameLabel: s.nameLabels.setting,
    secret: false,
  }),
  // Feature spec 17, phase 2: a network request's `<ALIAS>_HOST` and `<ALIAS>_PORT` (spec 11 6.15.24).
  entry({
    type: 'network-request',
    label: (l) =>
      l.label?.startsWith(strings.networks.requests.variableLocation)
        ? l.label
        : `${strings.networks.requests.variableLocation}${l.container ? ` · ${l.container}` : ''}`,
    icon: Network,
    tone: 'neutral',
    nameLabel: s.nameLabels.setting,
    secret: false,
  }),
  entry({
    type: 'platform',
    label: () => s.locationLabels.platform,
    icon: Settings2,
    tone: 'neutral',
    nameLabel: s.nameLabels.setting,
    secret: false,
  }),
  entry({
    type: 'secret',
    label: () => s.locationLabels.secret,
    icon: Lock,
    tone: 'warning',
    nameLabel: s.nameLabels.secretName,
    secret: true,
  }),
  entry({
    type: 'azure-key-vault',
    label: () => s.locationLabels.azureKeyVault,
    icon: SquareAsterisk,
    tone: 'info',
    nameLabel: s.nameLabels.secretName,
    secret: true,
    formatVersion: (version) => version.slice(0, 8),
    limitLabel: s.limits.keyVault,
    driftText: {
      expired: s.status.help.expired,
    },
    softDeleteDays: 90,
    vaultDeletion: (vault, deployment, until) =>
      strings.destroy.vaultSoftDelete(vault.name, 90, until, deployment, vault.region),
    revealText: (location, account) => [
      r.keyVault.before,
      ...(location.container ? [' ', { strong: location.container }] : []),
      ...readsAs(account),
      ' ',
      r.keyVault.after,
    ],
    disabledRevealText: r.keyVault.disabled,
  }),
  entry({
    type: 'aws-secrets-manager',
    label: () => v.aws.label,
    shortLabel: v.aws.shortLabel,
    icon: SquareAsterisk,
    tone: 'info',
    nameLabel: s.nameLabels.secretName,
    secret: true,
    formatVersion: (version) => version.slice(0, 8),
    limitLabel: v.aws.limitLabel,
    driftText: {
      rotated: v.aws.rotatedHelp,
    },
    softDeleteDays: 30,
    metadataNoun: 'tags',
    namespaceDeletion: { mechanism: v.aws.mechanism, caveat: v.aws.caveat },
    rotationText: (rotation) => ({
      text: rotation.enabled
        ? v.aws.rotationOn(rotation.nextRotationAt ? formatDay(rotation.nextRotationAt) : null)
        : v.aws.rotationOff,
      help: null,
    }),
    caseNote: v.aws.caseNote,
    placementText: v.aws.placement,
    prepareText: (deployment, prefix, region) => v.aws.prepare(deployment, prefix, region),
    revealText: (location, account) => [
      v.aws.reveal.before,
      ' ',
      { strong: location.container ? `${location.container}…` : v.aws.label },
      ` ${v.aws.reveal.from}`,
      ...readsAs(account),
      ' ',
      v.aws.reveal.after,
    ],
  }),
  entry({
    type: 'gcp-secret-manager',
    label: () => v.gcp.label,
    shortLabel: v.gcp.shortLabel,
    icon: SquareAsterisk,
    tone: 'info',
    nameLabel: v.secretId,
    secret: true,
    formatVersion: (version) => `v${version}`,
    limitLabel: v.gcp.limitLabel,
    driftText: {
      expired: v.gcp.expiredGeneric,
      disabled: v.gcp.disabled,
    },
    softDeleteDays: 30,
    metadataNoun: 'labels',
    namespaceDeletion: { mechanism: v.gcp.mechanism, caveat: v.gcp.caveat },
    rotationText: (rotation) => ({
      text: v.gcp.rotation(rotation.nextRotationAt ? formatDay(rotation.nextRotationAt) : null),
      help: v.gcp.rotationHelp,
    }),
    caseNote: v.gcp.caseNote,
    placementText: v.gcp.placement,
    prepareText: (deployment, _prefix, _region, project) => v.gcp.prepare(deployment, project),
    revealText: (location, account) => [
      v.gcp.reveal.before,
      ' ',
      { strong: location.container ? `${location.container}…` : v.gcp.label },
      ` ${v.gcp.reveal.from}`,
      ...readsAs(account),
      ' ',
      v.gcp.reveal.after,
    ],
    disabledRevealText: v.gcp.disabledReveal,
  }),
  ...STORE_KINDS.map(storeEntry),
  entry({
    type: 'pulumi-state',
    label: () => s.locationLabels.pulumiState,
    icon: Shield,
    tone: 'neutral',
    nameLabel: null,
    secret: true,
  }),
];

const LOCATION_MAP = new Map(LOCATION_TYPES.map((e) => [e.type, e]));

/** The generic entry for a location type the frontend doesn't know: the server's label, a key icon. */
export function genericLocation(location: Pick<SettingLocation, 'type' | 'secret'>): LocationTypeDefinition {
  return {
    type: location.type,
    label: (l) => l.label,
    icon: KeyRound,
    tone: 'neutral',
    nameLabel: s.nameLabels.generic,
    secret: location.secret,
    formatVersion: asIs,
    limitLabel: s.limits.generic,
    revealText: genericRevealText,
    disabledRevealText: r.generic.disabled,
    known: false,
  };
}

export function locationEntry(location: Pick<SettingLocation, 'type' | 'secret'>): LocationTypeDefinition {
  return LOCATION_MAP.get(location.type) ?? genericLocation(location);
}

/** The managed vault's location type per provider key; providers without one use the store fallback. */
export const MANAGED_VAULT_TYPES: Record<string, string> = {
  azure: 'azure-key-vault',
  aws: 'aws-secrets-manager',
  gcp: 'gcp-secret-manager',
};

/** The registry entry of a provider's managed vault, if the frontend knows it. */
export function managedVaultEntry(provider: string | null | undefined): LocationTypeDefinition | undefined {
  const type = provider ? MANAGED_VAULT_TYPES[provider] : undefined;
  return type ? LOCATION_MAP.get(type) : undefined;
}

/** The name of a provider's managed vault for sentences ("Azure Key Vault"); a generic name for others. */
export function managedVaultLabel(provider: string): string {
  const type = MANAGED_VAULT_TYPES[provider];
  const entry = type ? LOCATION_MAP.get(type) : undefined;
  return entry ? entry.label({ label: '', container: null }) : strings.createDeployment.genericVault;
}

/**
 * What a destroy does to a recorded vault of a location type (spec 6.4 Destroy): the registry's text or generic.
 * Namespace vaults (feature spec 15) use one template with the server's retention (`retentionDays`, 0 = at once,
 * `danger`) and name the unmanaged secrets that stay.
 */
export function vaultDeletionText(
  type: string | null | undefined,
  vault: { name: string; region: string },
  deployment: string,
  now = Date.now(),
  options: { retentionDays?: number | null; otherSecrets?: number } = {},
): { text: string; until: string | null; danger?: boolean } {
  const entry = type ? LOCATION_MAP.get(type) : undefined;
  if (entry?.namespaceDeletion) {
    const label = entry.label({ label: '', container: null });
    const days = options.retentionDays ?? entry.softDeleteDays ?? 0;
    const others = options.otherSecrets ? ` ${v.otherSecrets(options.otherSecrets, vault.name)}` : '';
    if (days <= 0) return { text: v.deletionNow(deployment, label) + others, until: null, danger: true };
    const until = new Date(now + days * 24 * 3600_000).toISOString();
    const text = v.deletion(
      v.deletionWhat(deployment, label, vault.name),
      formatDay(until),
      entry.namespaceDeletion.mechanism,
      deployment,
      vault.region,
      entry.shortLabel ?? label,
      entry.namespaceDeletion.caveat,
    );
    return { text: text + others, until };
  }
  if (entry?.vaultDeletion && entry.softDeleteDays) {
    const until = new Date(now + entry.softDeleteDays * 24 * 3600_000).toISOString();
    return { text: entry.vaultDeletion(vault, deployment, formatDay(until)), until };
  }
  const label = entry ? entry.label({ label: '', container: null }) : strings.destroy.genericVaultLabel;
  return { text: strings.destroy.vaultGeneric(vault.name, label), until: null };
}

/** The location's display label (registry wording, else the server's). */
export function locationLabel(
  location: Pick<SettingLocation, 'type' | 'secret' | 'label' | 'container'>,
): string {
  return locationEntry(location).label(location);
}

function joinApps(apps: readonly string[]): string {
  if (apps.length <= 1) return apps[0] ?? '';
  return `${apps.slice(0, -1).join(', ')} and ${apps[apps.length - 1]}`;
}

function azureDelivery(
  platform: string,
  name: string,
  labels: { reference: string; plain: string },
  refreshText: (app: string) => string = s.delivery.refreshGeneric,
) {
  const definition: DeliveryTypeDefinition = {
    platform,
    label: (mode) => (mode === 'reference' ? labels.reference : labels.plain),
    pickupText: (apps, delivery) =>
      delivery.mode === 'reference'
        ? s.delivery.pickupReference(name)
        : s.delivery.pickupPlain(joinApps(apps) || 'The app'),
    nowAction: (apps, delivery) =>
      delivery.mode === 'reference'
        ? {
            label: s.delivery.restartNow(joinApps(apps)),
            disruption: s.delivery.restartDisruption(apps.length > 1),
            default: delivery.nowAction?.default ?? 'from-on-change',
          }
        : null,
    readersText: (app, delivery) =>
      delivery.secret && delivery.mode === 'plain' ? s.delivery.readers(app) : null,
    refreshText,
    known: true,
  };
  return definition;
}

type NamespacePlatform = typeof strings.vaults.ecs | typeof strings.vaults.cloudRun;

/** ECS Express and Cloud Run (feature spec 15 6.9, 7.9): per-app statuses, rollouts instead of restarts. */
function namespaceDelivery(platform: string, t: NamespacePlatform): DeliveryTypeDefinition {
  return {
    platform,
    label: (mode) => (mode === 'reference' ? t.reference : t.plain),
    pickupText: (apps, delivery) =>
      delivery.mode === 'reference'
        ? t.pickup(joinApps(apps) || 'the app')
        : t.pickupPlain(joinApps(apps) || 'The app'),
    nowAction: (apps, delivery) =>
      delivery.mode === 'reference'
        ? { label: t.nowAction(joinApps(apps)), disruption: t.disruption, default: 'on' }
        : null,
    readersText: (_app, delivery) => delivery.readersText,
    refreshText: () => t.refreshFallback,
    known: true,
    nowActionShortLabel: () => t.nowShort,
    nowActionConfirm: false,
    nowActionDefaultReason: t.defaultReason,
    nowActionDoneText: (apps) => t.done(joinApps(apps)),
    nowActionFailedText: t.failed,
    statusText: { pending: t.pending, resolved: t.resolved, resolvedHelp: t.resolvedHelp },
    rawStatusText: t.raw,
    referenceFailureText: t.failure,
    grantDelayText: t.grantDelay,
    olderVersion: { label: t.older, help: t.olderHelp },
    rollingOutText: (_app, ago, by) => t.rollingOut(ago, by),
    settingsChangeText: t.settingsChange,
    refreshFallbackText: t.refreshFallback,
    activityAction: t.activity,
  };
}

/** App Service and Flex Consumption keep today's texts; the restart is confirmed (feature spec 15 19.2). */
function withRestart(definition: DeliveryTypeDefinition): DeliveryTypeDefinition {
  return {
    ...definition,
    nowActionShortLabel: (app) => s.delivery.restartNow(app).replace(/ now$/, ''),
    nowActionConfirm: true,
    activityAction: (app) => `restarted ${app}`,
    rollingOutText: (app) => `Restarting ${app}`,
  };
}

export const DELIVERY_PLATFORMS: readonly DeliveryTypeDefinition[] = [
  withRestart(
    azureDelivery('app-service', 'App Service', s.delivery.appService, s.delivery.refreshAppService),
  ),
  withRestart(azureDelivery('functions-flex', 'Flex Consumption', s.delivery.flex)),
  namespaceDelivery('ecs-express', strings.vaults.ecs),
  namespaceDelivery('cloud-run', strings.vaults.cloudRun),
];

const DELIVERY_MAP = new Map(DELIVERY_PLATFORMS.map((e) => [e.platform, e]));

/** The generic entry for an unknown or missing platform: the server's texts. */
export const GENERIC_DELIVERY: DeliveryTypeDefinition = {
  platform: null,
  label: (_mode, delivery) => delivery.label,
  pickupText: (_apps, delivery) => delivery.pickup,
  nowAction: (apps, delivery) =>
    delivery.nowAction
      ? {
          label: apps.length ? `${delivery.nowAction.label} (${joinApps(apps)})` : delivery.nowAction.label,
          disruption: delivery.nowAction.disruption,
          default: delivery.nowAction.default,
        }
      : null,
  readersText: (_app, delivery) => delivery.readersText,
  refreshText: s.delivery.refreshGeneric,
  known: false,
};

export function deliveryEntry(delivery: Pick<SettingDelivery, 'platform'>): DeliveryTypeDefinition {
  return (delivery.platform && DELIVERY_MAP.get(delivery.platform)) || GENERIC_DELIVERY;
}

export { joinApps };

/** "Tags" in the words of the location's metadata (GCP: labels, feature spec 15 19.1). */
export function metadataWords(
  text: string,
  location: Pick<SettingLocation, 'type'> | null | undefined,
): string {
  if (!location || LOCATION_MAP.get(location.type)?.metadataNoun !== 'labels') return text;
  return text
    .replace(/\bTags\b/g, 'Labels')
    .replace(/\btags\b/g, 'labels')
    .replace(/\btag\b/g, 'label');
}
